import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";

const EnrollmentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [participantType, setParticipantType] = useState("child"); // 'child' or 'adult'
  const [participants, setParticipants] = useState([
    {
      fullName: "",
      age: "",
      needsAssessment: true,
    },
  ]);
  const [contactInfo, setContactInfo] = useState({
    phone: "",
    email: "",
    address: "",
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [assessmentTimeSlot, setAssessmentTimeSlot] = useState("");
  const [errors, setErrors] = useState({
    contact: {
      phone: "",
      email: "",
      address: "",
    },
    participants: [],
    timeSlot: "",
    assessmentSlot: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddParticipant = () => {
    setParticipants([
      ...participants,
      { fullName: "", age: "", needsAssessment: true },
    ]);
    setErrors({
      ...errors,
      participants: [...errors.participants, { fullName: "", age: "" }],
    });
  };

  const handleRemoveParticipant = (index) => {
    const updatedParticipants = participants.filter((_, i) => i !== index);
    setParticipants(updatedParticipants);

    const updatedErrors = [...errors.participants];
    updatedErrors.splice(index, 1);
    setErrors({
      ...errors,
      participants: updatedErrors,
    });
  };

  const handleParticipantChange = (index, field, value) => {
    const updatedParticipants = [...participants];
    updatedParticipants[index][field] = value;
    setParticipants(updatedParticipants);

    if (errors.participants[index]?.[field]) {
      const updatedErrors = [...errors.participants];
      updatedErrors[index] = {
        ...updatedErrors[index],
        [field]: "",
      };
      setErrors({
        ...errors,
        participants: updatedErrors,
      });
    }
  };

  const handleContactChange = (field, value) => {
    setContactInfo({
      ...contactInfo,
      [field]: value,
    });

    if (errors.contact[field]) {
      setErrors({
        ...errors,
        contact: {
          ...errors.contact,
          [field]: "",
        },
      });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      contact: {
        phone: "",
        email: "",
        address: "",
      },
      participants: [],
      timeSlot: "",
      assessmentSlot: "",
    };

    // Validate contact info
    if (!contactInfo.phone.trim()) {
      newErrors.contact.phone = "Phone number is required";
      isValid = false;
    } else if (!/^[0-9]{10,15}$/.test(contactInfo.phone)) {
      newErrors.contact.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!contactInfo.email.trim()) {
      newErrors.contact.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.contact.email = "Please enter a valid email";
      isValid = false;
    }

    if (!contactInfo.address.trim()) {
      newErrors.contact.address = "Address is required";
      isValid = false;
    }

    // Validate participants
    participants.forEach((participant, index) => {
      const participantErrors = {
        fullName: "",
        age: "",
      };

      if (!participant.fullName.trim()) {
        participantErrors.fullName = "Full name is required";
        isValid = false;
      }

      if (!participant.age) {
        participantErrors.age = "Age is required";
        isValid = false;
      } else if (isNaN(participant.age)) {
        participantErrors.age = "Age must be a number";
        isValid = false;
      } else if (
        participantType === "child" &&
        (parseInt(participant.age) < 3 || parseInt(participant.age) > 16)
      ) {
        participantErrors.age = "For children, age must be between 3 and 16";
        isValid = false;
      } else if (
        participantType === "adult" &&
        parseInt(participant.age) < 16
      ) {
        participantErrors.age = "For adults, age must be 16 or above";
        isValid = false;
      }

      newErrors.participants.push(participantErrors);
    });

    // Validate time slot
    if (!selectedTimeSlot) {
      newErrors.timeSlot = "Please select a preferred time slot";
      isValid = false;
    }

    // Validate assessment slot if needed
    if (participants.some((p) => p.needsAssessment)) {
      if (!assessmentTimeSlot) {
        newErrors.assessmentSlot = "Please select an assessment time slot";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      try {
        // Prepare email template parameters
        const participantsList = participants
          .map(
            (p, idx) =>
              `Participant ${idx + 1}: ${p.fullName}, Age: ${
                p.age
              }, Needs Assessment: ${p.needsAssessment ? "Yes" : "No"}`
          )
          .join("\n");

        const templateParams = {
          plan_title: selectedPlan.title,
          plan_price: selectedPlan.price,
          plan_sessions: selectedPlan.sessions,
          participant_type: participantType === "child" ? "Children" : "Adults",
          participants_list: participantsList,
          contact_name: participants[0].fullName,
          contact_email: contactInfo.email,
          contact_phone: contactInfo.phone,
          contact_address: contactInfo.address,
          time_slot: selectedTimeSlot,
          assessment_slot: participants.some((p) => p.needsAssessment)
            ? assessmentTimeSlot
            : "Not Required",
          to_email: contactInfo.email,
        };

        // Send email using EmailJS
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );

        setIsSubmitting(false);
        navigate("/enrollment-success", {
          state: {
            enrollmentData: {
              plan: selectedPlan,
              participantType,
              participants,
              contactInfo,
              timeSlot: selectedTimeSlot,
              assessmentTimeSlot: participants.some((p) => p.needsAssessment)
                ? assessmentTimeSlot
                : null,
            },
          },
        });
      } catch (error) {
        console.error("Failed to send email:", error);
        setIsSubmitting(false);
        alert(
          "Failed to send enrollment email. Please try again or contact support."
        );
      }
    } else {
      setIsSubmitting(false);
    }
  };

  const selectedPlan = location.state?.selectedPlan || {
    id: "basic",
    title: "Basic Private Lessons",
    description: "Individual swimming instruction at your pool",
    price: "$120",
    sessions: "4 sessions/month",
    features: [
      "1-on-1 personalized swimming instruction",
      "Flexible scheduling around your availability",
      "Progress tracking and skill assessment",
      "Parent consultation and feedback",
      "All equipment provided by instructor",
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => {
              navigate(-1);
            }}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left h-5 w-5 mr-2"
            >
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back to Packages
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Your Enrollment
            </h1>
            <p className="text-xl text-gray-600">
              You're almost there! Just a few more details to get started.
            </p>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form
              className="bg-white rounded-2xl shadow-lg p-8"
              onSubmit={handleSubmit}
            >
              {/* Participant Type Selection */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-users h-6 w-6 text-blue-600 mr-2"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Participant Type
                  </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <label
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      participantType === "child"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <input
                      type="radio"
                      name="participantType"
                      className="sr-only"
                      value="child"
                      checked={participantType === "child"}
                      onChange={() => setParticipantType("child")}
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 mr-3 ${
                        participantType === "child"
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}
                    ></div>
                    <div>
                      <span className="text-gray-900 block font-medium">
                        Children (3-16 years)
                      </span>
                      <span className="text-sm text-gray-600">
                        Swimming lessons for kids and teens
                      </span>
                    </div>
                  </label>
                  <label
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      participantType === "adult"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <input
                      type="radio"
                      name="participantType"
                      className="sr-only"
                      value="adult"
                      checked={participantType === "adult"}
                      onChange={() => setParticipantType("adult")}
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 mr-3 ${
                        participantType === "adult"
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}
                    ></div>
                    <div>
                      <span className="text-gray-900 block font-medium">
                        Adults (16+ years)
                      </span>
                      <span className="text-sm text-gray-600">
                        Swimming lessons for adults
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-contact h-6 w-6 text-blue-600 mr-2"
                  >
                    <path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"></path>
                    <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                    <circle cx="12" cy="10" r="2"></circle>
                    <line x1="8" x2="8" y1="2" y2="4"></line>
                    <line x1="16" x2="16" y1="2" y2="4"></line>
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Contact Information
                  </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.contact.phone
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your phone number"
                      value={contactInfo.phone}
                      onChange={(e) =>
                        handleContactChange("phone", e.target.value)
                      }
                    />
                    {errors.contact.phone && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.contact.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.contact.email
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your email address"
                      value={contactInfo.email}
                      onChange={(e) =>
                        handleContactChange("email", e.target.value)
                      }
                    />
                    {errors.contact.email && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.contact.email}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Home Address
                  </label>
                  <textarea
                    id="address"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.contact.address
                        ? "border-red-300"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter your full home address including street, city, and postal code..."
                    value={contactInfo.address}
                    onChange={(e) =>
                      handleContactChange("address", e.target.value)
                    }
                  ></textarea>
                  {errors.contact.address && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.contact.address}
                    </p>
                  )}
                </div>
              </div>

              {/* Participants Information */}
              <div className="mb-8">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-users h-6 w-6 text-blue-600 mr-2"
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {participantType === "child"
                          ? "Children Information"
                          : "Participants Information"}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddParticipant}
                      className="flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-plus h-5 w-5 mr-1"
                      >
                        <path d="M5 12h14"></path>
                        <path d="M12 5v14"></path>
                      </svg>
                      Add{" "}
                      {participantType === "child" ? "Child" : "Participant"}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {participants.map((participant, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">
                            {participantType === "child"
                              ? `Child ${index + 1}`
                              : `Participant ${index + 1}`}
                          </h4>
                          {participants.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveParticipant(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-trash2 h-4 w-4"
                              >
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                <line x1="10" x2="10" y1="11" y2="17"></line>
                                <line x1="14" x2="14" y1="11" y2="17"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Full Name
                            </label>
                            <input
                              type="text"
                              value={participant.fullName}
                              onChange={(e) =>
                                handleParticipantChange(
                                  index,
                                  "fullName",
                                  e.target.value
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                errors.participants[index]?.fullName
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                              placeholder={`Enter ${
                                participantType === "child"
                                  ? "child's"
                                  : "participant's"
                              } full name`}
                            />
                            {errors.participants[index]?.fullName && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.participants[index].fullName}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Age
                            </label>
                            <input
                              type="number"
                              min={participantType === "child" ? "3" : "16"}
                              max="99"
                              value={participant.age}
                              onChange={(e) =>
                                handleParticipantChange(
                                  index,
                                  "age",
                                  e.target.value
                                )
                              }
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                errors.participants[index]?.age
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                              placeholder="Age"
                            />
                            {errors.participants[index]?.age && (
                              <p className="text-red-600 text-sm mt-1">
                                {errors.participants[index].age}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={participant.needsAssessment}
                              onChange={(e) => {
                                const updatedParticipants = [...participants];
                                updatedParticipants[index].needsAssessment =
                                  e.target.checked;
                                setParticipants(updatedParticipants);
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Needs level assessment test
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preferred Time Slot */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-clock h-6 w-6 text-blue-600 mr-2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Preferred Time Slot
                  </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    "6:00 AM - 7:00 AM (Early Bird)",
                    "7:00 AM - 8:00 AM (Morning)",
                    "9:00 AM - 10:00 AM (Mid-Morning)",
                    "10:00 AM - 11:00 AM (Late Morning)",
                    "4:00 PM - 5:00 PM (Afternoon)",
                    "5:00 PM - 6:00 PM (Evening)",
                    "6:00 PM - 7:00 PM (Late Evening)",
                    "7:00 PM - 8:00 PM (Night)",
                  ].map((slot) => (
                    <label
                      key={slot}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedTimeSlot === slot
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="timeSlot"
                        className="sr-only"
                        value={slot}
                        checked={selectedTimeSlot === slot}
                        onChange={() => {
                          setSelectedTimeSlot(slot);
                          if (errors.timeSlot) {
                            setErrors({ ...errors, timeSlot: "" });
                          }
                        }}
                      />
                      <div
                        className={`w-4 h-4 rounded-full border-2 mr-3 ${
                          selectedTimeSlot === slot
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      ></div>
                      <span className="text-gray-900">{slot}</span>
                    </label>
                  ))}
                </div>
                {errors.timeSlot && (
                  <p className="text-red-600 text-sm mt-2">{errors.timeSlot}</p>
                )}
              </div>

              {/* Assessment Time Slot (if needed) */}
              {participants.some((p) => p.needsAssessment) && (
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-clipboard-list h-6 w-6 text-blue-600 mr-2"
                    >
                      <rect
                        x="8"
                        y="2"
                        width="8"
                        height="4"
                        rx="1"
                        ry="1"
                      ></rect>
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <path d="M12 11h4"></path>
                      <path d="M12 16h4"></path>
                      <path d="M8 11h.01"></path>
                      <path d="M8 16h.01"></path>
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Level Assessment Test
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Please select a time for the level assessment test. This
                    will help us place{" "}
                    {participantType === "child" ? "your child" : "you"} in the
                    appropriate group.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      "Saturday 9:00 AM - 11:00 AM",
                      "Sunday 9:00 AM - 11:00 AM",
                      "Monday 4:00 PM - 6:00 PM",
                      "Wednesday 4:00 PM - 6:00 PM",
                    ].map((slot) => (
                      <label
                        key={slot}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          assessmentTimeSlot === slot
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="assessmentSlot"
                          className="sr-only"
                          value={slot}
                          checked={assessmentTimeSlot === slot}
                          onChange={() => {
                            setAssessmentTimeSlot(slot);
                            if (errors.assessmentSlot) {
                              setErrors({ ...errors, assessmentSlot: "" });
                            }
                          }}
                        />
                        <div
                          className={`w-4 h-4 rounded-full border-2 mr-3 ${
                            assessmentTimeSlot === slot
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300"
                          }`}
                        ></div>
                        <span className="text-gray-900">{slot}</span>
                      </label>
                    ))}
                  </div>
                  {errors.assessmentSlot && (
                    <p className="text-red-600 text-sm mt-2">
                      {errors.assessmentSlot}
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                className={`w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Complete Enrollment"
                )}
              </button>
            </form>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Enrollment Summary
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {selectedPlan.title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedPlan.description}
                  </p>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Monthly Fee</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {selectedPlan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedPlan.sessions}
                  </p>
                </div>
                <div className="border-t pt-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Included Features:
                  </h5>
                  <ul className="space-y-1">
                    {selectedPlan.features.map((feature, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-600 flex items-start"
                      >
                        <div className="bg-green-100 rounded-full p-0.5 mr-2 mt-1">
                          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentForm;
